import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Clock, AlarmClock, Moon, Lock, Save, SunMedium } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { scheduleMorningNotification, cancelMorningNotification } from "@/lib/native";

/**
 * MORNING — Iter 92. A CONFIGURATION experience, not a playback page.
 *
 * Iter 121 — no longer a standalone bottom-nav page: this content now lives
 * inside the Sessions screen (/rest) behind the Sleep/Morning selector.
 * All behavior (toggle, wake time, duration, save, continuity) is unchanged.
 *
 * Users configure once: Enable toggle + Wake-up Time + Duration → Save.
 * Morning sessions then start AUTOMATICALLY ~5 minutes before the wake-up
 * time, continuing whoever they fell asleep with (see MorningAutoStart.jsx).
 */
const SLIDER_MIN = 5;
const SLIDER_MAX = 90;
const SLIDER_STEP = 1;
const FREE_MAX_DURATION = 15;

export default function MorningContent({ onBeginNight }) {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [lastNight, setLastNight] = useState(undefined);
  const [sub, setSub] = useState(null);
  const [enabled, setEnabled] = useState(!!user?.morning_enabled);
  const [wakeTime, setWakeTime] = useState(user?.wake_time || "07:00");
  const [duration, setDuration] = useState(user?.morning_duration_minutes || 10);
  const [saving, setSaving] = useState(false);
  const isPremium = sub ? !!sub.is_premium : !!user?.is_premium;
  const sliderMax = sub?.max_duration_minutes ?? (isPremium ? SLIDER_MAX : FREE_MAX_DURATION);

  useEffect(() => {
    api.get("/sessions/last-night").then((r) => setLastNight(r.data)).catch(() => setLastNight({ exists: false }));
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    setEnabled(!!user.morning_enabled);
    if (user.wake_time) setWakeTime(user.wake_time);
    if (user.morning_duration_minutes) setDuration(user.morning_duration_minutes);
  }, [user]);

  // Continua 1.1 — Morning Sessions are an experimental PREMIUM feature:
  // 3 per calendar month, resetting monthly. Free/Light see a locked card.
  const morningsLeft = sub ? sub.remaining?.morning_sessions_this_month ?? null : null;
  // Cap shown in copy: the PREMIUM allowance (from the tier catalog), since the
  // locked card advertises what Premium offers — never the current tier's 0.
  const morningsCap =
    (sub?.tiers || []).find((t) => t.id === "premium")?.monthly_morning_sessions ??
    sub?.limits?.monthly_morning_sessions ?? 3;
  const morningsExhausted = isPremium && morningsLeft !== null && morningsLeft <= 0;

  const saveChanges = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/profile/morning", {
        morning_enabled: enabled,
        wake_time: wakeTime,
        morning_duration_minutes: Math.min(duration, sliderMax),
      });
      setUser(data);
      // Iter 96 — native reliability layer: a local notification at wake−5min
      // lets the OS wake Continua even if it was killed overnight (no-op on web).
      if (enabled) {
        scheduleMorningNotification({
          wakeTime,
          title: "Good morning",
          body: "Your companion is waking up beside you.",
        });
      } else {
        cancelMorningNotification();
      }
      toast("Changes saved successfully.");
    } catch (e) {
      toast("Couldn't save just now. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="morning-screen">
      <h1 className="font-serif-display text-5xl text-violet-50 mb-3 leading-none" data-testid="morning-title">
        Morning
      </h1>
      <p className="inline-flex items-center gap-1.5 rounded-full border border-amber-200/30 bg-amber-100/[0.05] px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-amber-200/85 mb-6" data-testid="morning-experimental-badge">
        <SunMedium className="w-3 h-3" strokeWidth={1.5} /> Experimental
      </p>

      {sub && !isPremium ? (
        <section className="rounded-2xl border border-violet-200/15 bg-violet-200/[0.04] px-5 py-6" data-testid="morning-locked-card">
          <p className="text-violet-50/90 text-sm flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-violet-300/50" strokeWidth={1.5} />
            Morning Sessions — Experimental
          </p>
          <p className="mt-3 text-xs text-violet-300/60 leading-relaxed">
            Morning Sessions are currently an experimental feature, available with
            Continua Premium. You can try up to {morningsCap} per month. After using one,
            we'd love to know what you think.
          </p>
          <p className="mt-2 text-xs text-violet-300/50 leading-relaxed">
            Your companion stays through the night and wakes up beside you — the
            morning continues whoever you fell asleep with.
          </p>
          <button
            type="button"
            onClick={() => nav("/upgrade")}
            data-testid="morning-upgrade-button"
            className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] border border-amber-300/35 text-amber-200/90 hover:bg-amber-300/10 hover:border-amber-300/55 transition-soft"
          >
            View plans
          </button>
        </section>
      ) : (
      <>
      {/* Enable toggle */}
      <section className="mb-8" data-testid="morning-enable-section">
        <div className="w-full flex items-center justify-between rounded-2xl border border-violet-200/20 bg-violet-200/5 px-5 py-4">
          <div className="pr-4">
            <p className="text-violet-50/90 text-sm">Enable Morning Sessions</p>
            <p className="text-[11px] text-violet-300/50 mt-1 leading-relaxed">
              Starts by itself about 5 minutes before your wake-up time, continuing whoever you fell asleep with.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="morning-enable-toggle"
            aria-label="Enable Morning Sessions"
          />
        </div>
      </section>

      {/* Wake-up time */}
      <section className="mb-8" data-testid="morning-waketime-section">
        <h2 className="text-xs uppercase tracking-[0.32em] text-violet-300/55 flex items-center gap-1.5 mb-3">
          <AlarmClock className="w-3 h-3" strokeWidth={1.5} /> Wake-up time
        </h2>
        <input
          type="time"
          value={wakeTime}
          onChange={(e) => setWakeTime(e.target.value)}
          data-testid="morning-waketime-input"
          aria-label="Wake-up time"
          className="w-full rounded-2xl border border-violet-200/20 bg-violet-200/5 px-5 py-4 text-violet-50 font-serif-display text-2xl tabular-nums focus:outline-none focus:border-violet-200/45 transition-colors [color-scheme:dark]"
        />
        <p className="mt-2 text-[11px] text-violet-300/50">
          The moment you should be awake — the world starts waking a few minutes earlier.
        </p>
        <p
          className="mt-3 text-[11px] text-amber-200/70 leading-relaxed"
          data-testid="morning-alarm-disclaimer"
        >
          Morning Sessions are not a replacement for an alarm clock. If you need to wake up at a specific time, please set a normal alarm as well.
        </p>
      </section>

      {/* Duration */}
      <section className="mb-8" data-testid="morning-duration-section">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-xs uppercase tracking-[0.32em] text-violet-300/55 flex items-center gap-1.5">
            <Clock className="w-3 h-3" strokeWidth={1.5} /> Duration
          </h2>
          <span className="font-serif-display text-2xl text-violet-50 tabular-nums" data-testid="morning-duration-value">
            {Math.min(duration, sliderMax)}<span className="text-sm text-violet-300/55 ml-1">min</span>
          </span>
        </div>
        <input
          type="range"
          min={SLIDER_MIN}
          max={sliderMax}
          step={SLIDER_STEP}
          value={Math.min(duration, sliderMax)}
          onChange={(e) => setDuration(Number(e.target.value))}
          data-testid="morning-duration-slider"
          aria-label="Morning session duration in minutes"
          className="continua-slider w-full"
        />
        <div className="flex justify-between text-[10px] uppercase tracking-[0.28em] text-violet-300/45 mt-1.5">
          <span>{SLIDER_MIN} min</span>
          <span>{sliderMax} min</span>
        </div>
      </section>

      {/* Save */}
      <button
        type="button"
        onClick={saveChanges}
        disabled={saving}
        data-testid="morning-save-button"
        className={`w-full flex items-center justify-center gap-2 rounded-full border border-violet-200/35 bg-violet-200/10 px-6 py-3.5 text-sm text-violet-50 transition-all mb-10 ${
          saving ? "opacity-60" : "hover:border-violet-100/55 hover:bg-violet-200/16 active:scale-[0.99]"
        }`}
      >
        <Save className="w-4 h-4" strokeWidth={1.5} />
        {saving ? "Saving…" : "Save Changes"}
      </button>

      {/* Continuity card — who the morning will continue with */}
      <section className="mb-6" data-testid="morning-continuity-section">
        <h2 className="text-xs uppercase tracking-[0.32em] text-violet-300/55 flex items-center gap-1.5 mb-3">
          <Moon className="w-3 h-3" strokeWidth={1.5} /> Continuing last night
        </h2>
        {lastNight?.exists ? (
          <div className="w-full rounded-2xl border border-violet-200/20 bg-violet-200/5 px-5 py-4" data-testid="morning-inherited-card">
            <p className="text-violet-50/90 text-sm" data-testid="morning-inherited-companion">
              You'll wake up with <span className="font-serif-display text-base text-violet-50">{lastNight.character_name || "your companion"}</span>
            </p>
            <p className="text-xs text-violet-300/55 mt-1" data-testid="morning-inherited-atmosphere">
              {lastNight.atmosphere_name || "The quiet place"} · still there when the sun comes up
            </p>
            {lastNight.memory_title && (
              <p className="text-[11px] text-violet-300/40 mt-2 italic" data-testid="morning-inherited-memory">
                “{lastNight.memory_title}”
              </p>
            )}
          </div>
        ) : (
          <div className="w-full rounded-2xl border border-violet-200/12 bg-violet-200/3 px-5 py-4" data-testid="morning-no-night-card">
            <p className="text-xs text-violet-300/55 leading-relaxed">
              No night yet. Start a Sleep Session this evening — whoever you fall asleep with will be there in the morning.
            </p>
            <button
              type="button"
              onClick={onBeginNight}
              data-testid="morning-go-to-rest-button"
              className="mt-3 text-xs text-violet-200/80 underline underline-offset-4 hover:text-violet-100 transition-colors"
            >
              Begin a night
            </button>
          </div>
        )}
      </section>

      {morningsLeft !== null && (
        <p
          className={`text-[11px] flex items-center gap-1.5 ${morningsExhausted ? "text-violet-200/70" : "text-violet-300/50"}`}
          data-testid="morning-remaining"
        >
          {morningsExhausted && <Lock className="w-3 h-3" strokeWidth={1.5} />}
          {morningsExhausted
            ? `You've used all ${morningsCap} experimental Morning Sessions this month. They return at the start of next month.`
            : `${morningsLeft} of ${morningsCap} experimental Morning Sessions left this month. A morning only counts once it actually plays.`}
        </p>
      )}
      </>
      )}
    </div>
  );
}
